import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar, DollarSign, User, Phone, CreditCard, Percent, Trash2, TrendingUp, Edit2, Check, X, MessageCircle, AlertTriangle } from "lucide-react";
import { useCurrency } from "@/hooks/useUserCurrency";
import { format, differenceInCalendarDays, startOfDay, differenceInCalendarMonths } from "date-fns";
import { ptBR } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { maskCPF, maskPhone } from "@/lib/privacy";
import { LoanNotes } from "@/components/LoanNotes";
import { useIsMobile } from "@/hooks/use-mobile";
import { z } from "zod";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useEffect, useState } from "react";
import { Star, Pencil } from "lucide-react";
import { getInstallmentAmount, hasCustomInstallments, remainingBalance } from "@/lib/installments";
import { EditInstallmentsDialog } from "@/components/EditInstallmentsDialog";

interface LoanCardProps {
  id: string;
  clientName: string;
  amount: number;
  dueDate: Date | string;
  status: "paid" | "active" | "overdue";
  interestRate: number;
  interestType: "monthly" | "fixed" | "daily";
  installments: number;
  paidInstallments: number;
  phone: string;
  cpf: string;
  loanDate: Date | string;
  clientId?: string;
  dailyPenalty?: number;
  installmentFrequency?: "weekly" | "biweekly" | "monthly";
  installmentValues?: unknown;
  onDelete?: () => void;
  score?: { score: number; label: string; tone: "success" | "warning" | "destructive" };
}

const clientUpdateSchema = z.object({
  phone: z.string().trim().regex(/^\(\d{2}\)\s\d{4,5}-\d{4}$/, "Formato: (99) 99999-9999").optional().or(z.literal("")),
  cpf: z.string().trim().regex(/^\d{3}\.\d{3}\.\d{3}-\d{2}$/, "Formato: 999.999.999-99").optional().or(z.literal("")),
});

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`Tempo esgotado: ${label}`)), ms)
    ),
  ]);
}


export function LoanCard({ id, clientName, amount, dueDate, status, interestRate, interestType, installments, paidInstallments, phone, cpf, loanDate, clientId, dailyPenalty = 0, installmentFrequency = "monthly", installmentValues, onDelete, score }: LoanCardProps) {
  const { format: fmtMoney, country: fmtCountry } = useCurrency();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editInstallmentsOpen, setEditInstallmentsOpen] = useState(false);
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [editPhone, setEditPhone] = useState(phone || "");
  const [editCpf, setEditCpf] = useState(cpf || "");
  const [hasUndoPayment, setHasUndoPayment] = useState(false);

  // Edição do empréstimo
  const [isEditingLoan, setIsEditingLoan] = useState(false);
  const [isSavingLoan, setIsSavingLoan] = useState(false);
  const [editName, setEditName] = useState(clientName || "");
  const [editAmount, setEditAmount] = useState(String(amount ?? ""));
  const [editInterestRate, setEditInterestRate] = useState(String(interestRate ?? ""));
  const [editDailyPenalty, setEditDailyPenalty] = useState(String(dailyPenalty ?? 0));
  const toIsoDate = (d: Date | string) => {
    if (d instanceof Date) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${dd}`;
    }
    return String(d);
  };
  const [editLoanDate, setEditLoanDate] = useState(toIsoDate(loanDate as any));
  const [editDueDate, setEditDueDate] = useState(toIsoDate(dueDate as any));

  useEffect(() => {
    if (!open) return;

    const checkUndoAvailability = async () => {
      const { data } = await supabase
        .from("loan_payments")
        .select("id")
        .eq("loan_id", id)
        .order("paid_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      setHasUndoPayment(!!data?.id);
    };

    checkUndoAvailability();
  }, [open, id]);
  const toLocalDate = (d: Date | string) => {
    if (d instanceof Date) return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    if (typeof d === "string") {
      const [y, m, dd] = d.split("-").map(Number);
      return new Date(y, (m || 1) - 1, dd || 1);
    }
    return new Date(d as any);
  };

  const now = startOfDay(new Date());
  const due = toLocalDate(dueDate as any);
  const loan = toLocalDate(loanDate as any);

  const actualStatus = status === "active" && due < now ? "overdue" : status;
  
  // Calculate days until due for WhatsApp reminder (positive = days until due, negative = days overdue)
  const daysUntilDue = differenceInCalendarDays(due, now);
  const isNearDue = actualStatus === "active" && daysUntilDue >= 0 && daysUntilDue <= 7;
  const showWhatsAppButton = (actualStatus === "overdue" || isNearDue) && phone;

  // Juros mensais fixos e dias em atraso
  const monthlyInterest = amount * (interestRate / 100);
  const overdueDays = due < now ? differenceInCalendarDays(now, due) : 0;
  
  // Calcular meses completos de atraso baseado no dia de vencimento
  const overdueMonths = due < now ? (() => {
    const monthsDiff = differenceInCalendarMonths(now, due);
    // Se o dia atual é >= ao dia de vencimento, o mês atual também conta
    if (now.getDate() >= due.getDate()) {
      return monthsDiff + 1;
    }
    return monthsDiff;
  })() : 0;
  
  // Fixo agora é sem juros e sem multa por atraso; mensal usa juros mensais
  // (fixedLateCharge é declarado mais abaixo)

  // Valor da parcela corrente do empréstimo fixo (sem juros).
  // Usa valores personalizados quando existirem; senão, valor total ÷ parcelas.
  const installmentLoanLike = { amount, installments, installmentValues };
  const fixedInstallmentAmount =
    interestType === "fixed" && installments > 0
      ? getInstallmentAmount(installmentLoanLike, paidInstallments)
      : 0;
  const fixedRemainingBalance =
    interestType === "fixed" ? remainingBalance(installmentLoanLike, paidInstallments) : 0;

  // Avança/recua uma data conforme a periodicidade das parcelas
  const advanceByFrequency = (date: Date, direction: 1 | -1) => {
    const next = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    if (installmentFrequency === "weekly") {
      next.setDate(next.getDate() + 7 * direction);
    } else if (installmentFrequency === "biweekly") {
      next.setDate(next.getDate() + 15 * direction);
    } else {
      next.setMonth(next.getMonth() + direction);
    }
    return next;
  };

  // === Modalidade "Diário" ===
  // Modelo por ciclo: ciclo = dias entre loanDate e dueDate.
  // A cada ciclo vencido cobra 1 juros completo (amount * rate).
  // Após vencimento, cobra também diária de atraso.
  const diffDays = Math.max(1, differenceInCalendarDays(due, loan) || 1);
  const dailyCycleDays =
    interestType === "daily"
      ? Math.max(1, installments || diffDays)
      : 0;
  const dailyInterestPerCycle =
    interestType === "daily" ? amount * (interestRate / 100) : 0;
  // ciclos vencidos: 1 ao vencer, +1 a cada cycleDays de atraso
  const dailyCyclesElapsed =
    interestType === "daily" && overdueDays >= 0 && due <= now
      ? 1 + Math.floor(overdueDays / dailyCycleDays)
      : 0;
  const dailyOverdueInterest = dailyInterestPerCycle * dailyCyclesElapsed;
  const dailyLatePenalty = overdueDays > 0 ? overdueDays * (dailyPenalty || 0) : 0;
  const dailyTotalCharges = dailyOverdueInterest + dailyLatePenalty;
  const dailyTotalToSettle = amount + dailyTotalCharges;
  const fixedOverdueInstallments = interestType === "fixed" && overdueDays > 0
    ? Math.min(
        installmentFrequency === "weekly"
          ? 1 + Math.floor(overdueDays / 7)
          : installmentFrequency === "biweekly"
          ? 1 + Math.floor(overdueDays / 15)
          : Math.max(1, overdueMonths),
        Math.max(0, installments - paidInstallments),
      )
    : 0;
  const fixedLateCharge = interestType === "fixed" && overdueDays > 0
    ? ((fixedOverdueInstallments - 1) * fixedInstallmentAmount) + (overdueDays * (dailyPenalty || 0))
    : 0;
  const totalInterest = interestType === "fixed" ? fixedLateCharge : monthlyInterest;

  const handleDelete = async () => {
    setIsUpdating(true);
    console.log("[handleDelete] removendo empréstimo", { id });
    try {
      // Marca como quitado para que o cliente apareça no Remarketing
      // preservando os dados originais (valor, datas) e o dia da quitação.
      const { data: { session } } = await withTimeout(supabase.auth.getSession(), 10000, "sessão");
      const user = session?.user;
      if (!user) {
        toast.error("Usuário não autenticado");
        return;
      }

      const nowIso = new Date().toISOString();
      let juros = 0;
      let diaria = 0;
      if (interestType === "daily") {
        juros = dailyOverdueInterest;
        diaria = dailyLatePenalty;
      } else if (interestType === "fixed") {
        juros = 0;
        diaria = 0;
      } else {
        const mesesCobrados = overdueMonths > 0 ? overdueMonths : 1;
        juros = monthlyInterest * mesesCobrados;
        diaria = overdueDays > 0 ? overdueDays * (dailyPenalty || 0) : 0;
      }

      // Registra pagamento final (mantém histórico p/ score e Remarketing)
      const { error: payErr } = await withTimeout(
        (async () => supabase.from("loan_payments").insert({
          user_id: user.id,
          loan_id: id,
          amount: amount + juros + diaria,
          interest_amount: juros,
          penalty_amount: diaria,
          principal_amount: amount,
          paid_at: nowIso,
          is_settlement: true,
        } as any))(),
        15000,
        "registrar quitação"
      );
      if (payErr) {
        console.warn("[handleDelete] aviso ao registrar pagamento", payErr);
      }

      // Atualiza o empréstimo como pago, sem alterar valor/datas originais
      const { error } = await withTimeout(
        (async () => supabase.from("loans").update({ status: "paid", updated_at: nowIso, settled_at: nowIso } as any).eq("id", id).eq("user_id", user.id))(),
        15000,
        "quitar empréstimo"
      );

      if (error) {
        console.error("[handleDelete] erro ao quitar", error);
        toast.error(`Erro ao remover empréstimo: ${error.message}`);
        return;
      }

      toast.success("Empréstimo encerrado! Cliente disponível no Remarketing.");
      setOpen(false);
      onDelete?.();
    } catch (e: any) {
      console.error("[handleDelete] exceção inesperada", e);
      toast.error("Não foi possível concluir. Verifique sua conexão e tente novamente.");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleStatusUpdate = async (newStatus: "paid") => {
    setIsUpdating(true);

    let juros = 0;
    let diaria = 0;
    if (interestType === "daily") {
      juros = dailyOverdueInterest;
      diaria = dailyLatePenalty;
    } else if (interestType === "fixed") {
      juros = 0;
      diaria = fixedLateCharge;
    } else {
      const mesesCobrados = overdueMonths > 0 ? overdueMonths : 1;
      juros = monthlyInterest * mesesCobrados;
      diaria = overdueDays > 0 ? overdueDays * (dailyPenalty || 0) : 0;
    }

    try {
      const { data: { session } } = await withTimeout(supabase.auth.getSession(), 10000, "sessão");
      const user = session?.user;

      if (!user) {
        toast.error("Usuário não autenticado");
        return;
      }

      // Com parcelas personalizadas, registra o valor exato da parcela corrente.
      // Sem personalização, mantém exatamente o comportamento anterior.
      const valorRegistrado =
        interestType === "fixed" && hasCustomInstallments(installmentLoanLike)
          ? fixedInstallmentAmount
          : amount;

      const { error: paymentError } = await withTimeout(
        (async () => supabase.from("loan_payments").insert({
          user_id: user.id,
          loan_id: id,
          amount: valorRegistrado,
          interest_amount: juros,
          penalty_amount: diaria,
          principal_amount: valorRegistrado,
          paid_at: new Date().toISOString(),
        } as any))(),
        15000,
        "registrar pagamento"
      );

      if (paymentError) {
        toast.error("Erro ao registrar pagamento");
        return;
      }

      const { error } = await withTimeout(
        (async () => supabase.from("loans").update({ status: newStatus }).eq("id", id))(),
        15000,
        "atualizar status"
      );


      if (error) {
        toast.error("Erro ao atualizar status");
        return;
      }

      setHasUndoPayment(true);
      toast.success("Pagamento registrado com sucesso!");
      onDelete?.();
    } catch (e: any) {
      console.error("[handleStatusUpdate] erro/timeout", e);
      toast.error("A operação demorou demais. Verifique sua conexão e tente novamente.");
    } finally {
      setIsUpdating(false);
    }
  };


  const handleUndoPayment = async () => {
    setIsUpdating(true);

    try {
      const { data: currentLoan, error: loanError } = await supabase
        .from("loans")
        .select("due_date, status, interest_type, installments, paid_installments")
        .eq("id", id)
        .single();

      if (loanError || !currentLoan) {
        toast.error("Erro ao buscar estado atual do empréstimo");
        setIsUpdating(false);
        return;
      }

      const { data: lastPayment } = await supabase
        .from("loan_payments")
        .select("id")
        .eq("loan_id", id)
        .order("paid_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!lastPayment) {
        setHasUndoPayment(false);
        toast.error("Nenhum pagamento para desfazer");
        setIsUpdating(false);
        return;
      }

      const { error: deletePaymentError } = await supabase
        .from("loan_payments")
        .delete()
        .eq("id", lastPayment.id);

      if (deletePaymentError) {
        toast.error("Erro ao desfazer pagamento");
        setIsUpdating(false);
        return;
      }

      const currentDueDate = toLocalDate(currentLoan.due_date);
      const shouldRollbackDueDate = currentLoan.interest_type === "monthly" || currentLoan.status !== "paid";

      const restoredDueDate = shouldRollbackDueDate
        ? (currentLoan.interest_type === "fixed"
            ? advanceByFrequency(currentDueDate, -1)
            : new Date(currentDueDate.getFullYear(), currentDueDate.getMonth() - 1, currentDueDate.getDate()))
        : currentDueDate;

      const restoredStatus = restoredDueDate < startOfDay(new Date()) ? "overdue" : "active";

      const updateData: {
        due_date: string;
        status: "active" | "overdue";
        paid_installments?: number;
      } = {
        due_date: format(restoredDueDate, "yyyy-MM-dd"),
        status: restoredStatus,
      };

      if (currentLoan.interest_type === "fixed") {
        updateData.paid_installments = Math.max((currentLoan.paid_installments ?? 0) - 1, 0);
      }

      const { error: restoreLoanError } = await supabase
        .from("loans")
        .update(updateData)
        .eq("id", id);

      if (restoreLoanError) {
        toast.error("Erro ao restaurar estado anterior");
        setIsUpdating(false);
        return;
      }

      const { data: remainingPayment } = await supabase
        .from("loan_payments")
        .select("id")
        .eq("loan_id", id)
        .order("paid_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      setHasUndoPayment(!!remainingPayment?.id);
      toast.success("Pagamento desfeito com sucesso");
      onDelete?.();
    } catch {
      toast.error("Erro ao desfazer pagamento");
    }

    setIsUpdating(false);
  };

  const handleContactUpdate = async () => {
    // Validação
    const result = clientUpdateSchema.safeParse({
      phone: editPhone,
      cpf: editCpf,
    });

    if (!result.success) {
      const errors = result.error.flatten().fieldErrors;
      if (errors.phone) toast.error(errors.phone[0]);
      if (errors.cpf) toast.error(errors.cpf[0]);
      return;
    }

    if (!clientId) {
      toast.error("ID do cliente não encontrado");
      return;
    }

    // Atualizar no banco - sempre enviar os campos, mesmo vazios
    const updateData = {
      phone: editPhone.trim() || null,
      cpf: editCpf.trim() || null,
    };

    const { error } = await supabase
      .from("clients")
      .update(updateData)
      .eq("id", clientId);

    if (error) {
      console.error("Erro ao atualizar cliente:", error);
      toast.error("Erro ao atualizar dados");
      return;
    }

    toast.success("Dados atualizados com sucesso!");
    setIsEditingContact(false);
    onDelete?.(); // Refetch data
  };

  const handleCancelEdit = () => {
    setEditPhone(phone || "");
    setEditCpf(cpf || "");
    setIsEditingContact(false);
  };

  const startEditLoan = () => {
    setEditName(clientName || "");
    setEditAmount(String(amount ?? ""));
    setEditInterestRate(String(interestRate ?? ""));
    setEditDailyPenalty(String(dailyPenalty ?? 0));
    setEditLoanDate(toIsoDate(loanDate as any));
    setEditDueDate(toIsoDate(dueDate as any));
    setEditPhone(phone ? formatPhoneInput(phone) : "");
    setEditCpf(cpf ? formatCpfInput(cpf) : "");
    setIsEditingLoan(true);
  };

  const cancelEditLoan = () => {
    setIsEditingLoan(false);
  };

  const handleSaveLoan = async () => {
    // Validação
    const amountNum = Number(String(editAmount).replace(",", "."));
    const rateNum = Number(String(editInterestRate).replace(",", "."));

    if (!editName.trim()) {
      toast.error("Nome do cliente é obrigatório");
      return;
    }
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      toast.error("Valor emprestado inválido");
      return;
    }
    if (!Number.isFinite(rateNum) || rateNum < 0) {
      toast.error("Taxa de juros inválida");
      return;
    }
    if (!editLoanDate || !editDueDate) {
      toast.error("Datas são obrigatórias");
      return;
    }
    if (editDueDate < editLoanDate) {
      toast.error("Vencimento não pode ser anterior à data do empréstimo");
      return;
    }
    const cpfDigits = editCpf.replace(/\D/g, "");
    if (cpfDigits && cpfDigits.length !== 11) {
      toast.error("CPF inválido. Deve conter 11 dígitos");
      return;
    }
    const phoneDigits = editPhone.replace(/\D/g, "");
    if (phoneDigits && (phoneDigits.length < 10 || phoneDigits.length > 11)) {
      toast.error("Telefone inválido. Deve conter 10 ou 11 dígitos");
      return;
    }
    const dailyPenaltyNum = editDailyPenalty.trim() === "" ? 0 : Number(String(editDailyPenalty).replace(",", "."));
    if (!Number.isFinite(dailyPenaltyNum) || dailyPenaltyNum < 0) {
      toast.error("Diária de atraso inválida");
      return;
    }

    setIsSavingLoan(true);

    // Atualiza empréstimo
    const { error: loanError } = await supabase
      .from("loans")
      .update({
        amount: amountNum,
        interest_rate: rateNum,
        loan_date: editLoanDate,
        due_date: editDueDate,
        daily_penalty: dailyPenaltyNum,
      })
      .eq("id", id);

    if (loanError) {
      console.error(loanError);
      toast.error("Erro ao atualizar empréstimo");
      setIsSavingLoan(false);
      return;
    }

    // Atualiza cliente (nome / cpf / telefone) se houver clientId
    if (clientId) {
      const { error: clientError } = await supabase
        .from("clients")
        .update({
          name: editName.trim(),
          cpf: cpfDigits ? formatCpfInput(cpfDigits) : null,
          phone: phoneDigits ? formatPhoneInput(phoneDigits) : null,
        })
        .eq("id", clientId);

      if (clientError) {
        console.error(clientError);
        toast.error("Empréstimo salvo, mas houve erro ao atualizar o cliente");
        setIsSavingLoan(false);
        setIsEditingLoan(false);
        onDelete?.();
        return;
      }
    }

    toast.success("Empréstimo atualizado com sucesso!");
    setIsSavingLoan(false);
    setIsEditingLoan(false);
    onDelete?.();
  };

  const formatPhoneInput = (value: string) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3");
    }
    return numbers.replace(/(\d{2})(\d{5})(\d{0,4})/, "($1) $2-$3");
  };

  const formatCpfInput = (value: string) => {
    const numbers = value.replace(/\D/g, "");
    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, "$1.$2.$3-$4");
  };
  
  const statusConfig = {
    paid: {
      label: "Pago",
      variant: "default" as const,
      className: "bg-success text-success-foreground hover:bg-success",
    },
    active: {
      label: "Em dia",
      variant: "secondary" as const,
      className: "bg-primary/10 text-primary hover:bg-primary/20",
    },
    overdue: {
      label: "Vencido",
      variant: "destructive" as const,
      className: "bg-overdue text-overdue-foreground hover:bg-overdue",
    },
  };

  const displayConfig = statusConfig[actualStatus];

  // WhatsApp reminder function
  const handleWhatsAppReminder = () => {
    if (!phone) {
      toast.error("Cliente não possui telefone cadastrado");
      return;
    }
    
    // Sanitize phone number: remove all non-digits
    const sanitizedPhone = phone.replace(/\D/g, "");
    
    // Add country code 55 if not present
    const fullPhone = sanitizedPhone.startsWith("55") ? sanitizedPhone : `55${sanitizedPhone}`;
    
    // Format currency helper
    const formatCurrency = (value: number) => 
      value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    
    let message: string;
    
    if (actualStatus === "overdue") {
      const principal = amount;
      let accruedInterest: number;
      let lateFee: number;
      if (interestType === "daily") {
        // Usa exatamente o mesmo cálculo do painel diário/recorrente
        accruedInterest = dailyOverdueInterest;
        lateFee = dailyLatePenalty;
      } else if (interestType === "fixed") {
        accruedInterest = 0;
        lateFee = fixedLateCharge;
      } else {
        accruedInterest = principal * (interestRate / 100) * overdueMonths;
        const monthlyDailyPenalty = (dailyPenalty && dailyPenalty > 0) ? dailyPenalty : 10;
        lateFee = overdueDays * monthlyDailyPenalty;
      }
      const totalInterestCalc = accruedInterest + lateFee;
      const totalTerminal = principal + totalInterestCalc;

      if (interestType === "daily") {
        message = `Olá ${clientName}, seu pagamento está vencido.

Valor emprestado: ${fmtCountry.currencySymbol} ${formatCurrency(principal)}
Juros vencidos: ${fmtCountry.currencySymbol} ${formatCurrency(accruedInterest)}
Diárias de atraso: ${fmtCountry.currencySymbol} ${formatCurrency(lateFee)}
Total juros + diárias: ${fmtCountry.currencySymbol} ${formatCurrency(totalInterestCalc)}
Total para terminar: ${fmtCountry.currencySymbol} ${formatCurrency(totalTerminal)}

Pix: 21966346798

Envie o comprovante depois de pago.`;
      } else if (interestType === "fixed") {
        const parcelaTotal = (fixedOverdueInstallments * fixedInstallmentAmount) + (overdueDays * (dailyPenalty || 0));
        message = `Olá ${clientName}, sua parcela venceu.

*Resolva logo sua pendência.*

Parcelas vencidas: ${fixedOverdueInstallments}x de ${fmtCountry.currencySymbol} ${formatCurrency(fixedInstallmentAmount)}
Diária de atraso (${overdueDays} dia(s)): ${fmtCountry.currencySymbol} ${formatCurrency(overdueDays * (dailyPenalty || 0))}

Total para pagar: ${fmtCountry.currencySymbol} ${formatCurrency(parcelaTotal)}

Pix: 21966346798

Envie o comprovante depois de pago.`;
      } else {
        message = `Olá ${clientName}, seu pagamento venceu.

*Resolva logo sua pendência.*

Juros + Multa: ${fmtCountry.currencySymbol} ${formatCurrency(totalInterestCalc)}

Total pra terminar: ${fmtCountry.currencySymbol} ${formatCurrency(totalTerminal)}

Pix: 21966346798

Envie o comprovante depois de pago.`;
      }
    } else if (daysUntilDue === 0) {
      const formattedAmount = fmtMoney(amount);
      const formattedDate = format(due, "dd/MM/yyyy", { locale: ptBR });
      message = `Olá ${clientName}, lembramos que o pagamento de ${formattedAmount} vence hoje (${formattedDate}). Podemos contar com você?`;
    } else {
      const formattedAmount = fmtMoney(amount);
      const formattedDate = format(due, "dd/MM/yyyy", { locale: ptBR });
      message = `Olá ${clientName}, lembramos que o pagamento de ${formattedAmount} vence em ${daysUntilDue} dia${daysUntilDue > 1 ? 's' : ''} (${formattedDate}). Podemos contar com você?`;
    }
    
    // Create WhatsApp URL
    const whatsappUrl = `https://wa.me/${fullPhone}?text=${encodeURIComponent(message)}`;
    
    window.open(whatsappUrl, "_blank");
  };
  
  // Get WhatsApp button label and style based on status
  const getWhatsAppButtonConfig = () => {
    if (actualStatus === "overdue") {
      return {
        label: "Cobrar no WhatsApp",
        className: "bg-green-500 hover:bg-green-600",
        iconColor: "text-green-600 hover:text-green-700",
        borderColor: "border-green-500 bg-green-500/10 hover:bg-green-500/20"
      };
    }
    // Near due - use amber/yellow for warning
    return {
      label: daysUntilDue === 0 ? "Lembrar Hoje" : `Lembrar (${daysUntilDue}d)`,
      className: "bg-amber-500 hover:bg-amber-600",
      iconColor: "text-amber-600 hover:text-amber-700",
      borderColor: "border-amber-500 bg-amber-500/10 hover:bg-amber-500/20"
    };
  };
  
  const whatsAppConfig = getWhatsAppButtonConfig();

  // Avatar gradient (deterministic per client)
  const avatarClasses = ["avatar-grad-1","avatar-grad-2","avatar-grad-3","avatar-grad-4","avatar-grad-5"];
  const avatarKey = clientId || clientName || id;
  let __h = 0;
  for (let i = 0; i < avatarKey.length; i++) __h = (__h * 31 + avatarKey.charCodeAt(i)) | 0;
  const avatarClass = avatarClasses[Math.abs(__h) % avatarClasses.length];
  const clientInitials = (clientName || "?")
    .split(" ").filter(Boolean).slice(0,2).map(p => p[0]).join("").toUpperCase();

  // Top-right status badge (Em dia / Vence em N dias / Vencido)
  const headerBadge = actualStatus === "paid"
    ? { txt: "Pago", cls: "bg-[hsl(159_83%_34%/0.13)] text-[hsl(159_83%_28%)]" }
    : actualStatus === "overdue"
    ? { txt: "Vencido", cls: "bg-[hsl(4_70%_59%/0.14)] text-[hsl(4_70%_45%)]" }
    : daysUntilDue <= 7
    ? { txt: `Vence em ${daysUntilDue} dia${daysUntilDue === 1 ? "" : "s"}`, cls: "bg-[hsl(28_92%_44%/0.16)] text-[hsl(28_92%_38%)]" }
    : { txt: "Em dia", cls: "bg-[hsl(159_83%_34%/0.13)] text-[hsl(159_83%_28%)]" };

  // Score bar gradient
  const scoreVal = score?.score ?? 0;
  const scoreBarGradient =
    scoreVal >= 90 ? "linear-gradient(90deg, hsl(159 83% 38%), hsl(159 83% 34%))"
    : scoreVal >= 70 ? "linear-gradient(90deg, hsl(28 92% 50%), hsl(159 83% 38%))"
    : "linear-gradient(90deg, hsl(4 70% 59%), hsl(28 92% 50%))";

  return (
    <Card className="surface-card surface-hover overflow-hidden transition-all border-0">
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`h-11 w-11 rounded-xl ${avatarClass} flex items-center justify-center text-[13px] font-bold shrink-0 shadow-[0_6px_16px_-8px_hsl(225_60%_30%/0.4)]`}>
              {clientInitials}
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-[16px] text-foreground truncate leading-tight">{clientName}</h3>
              <div className="flex items-center gap-1 mt-1 text-[12px] text-muted-foreground">
                <Phone className="h-3 w-3 shrink-0" />
                <span className="truncate">{maskPhone(phone)}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {showWhatsAppButton && !isMobile && (
              <Button
                size="icon"
                variant="outline"
                onClick={handleWhatsAppReminder}
                className={`h-8 w-8 ${whatsAppConfig.borderColor} ${whatsAppConfig.iconColor}`}
                title={actualStatus === "overdue" ? "Enviar cobrança via WhatsApp" : "Enviar lembrete via WhatsApp"}
              >
                <MessageCircle className="h-4 w-4" />
              </Button>
            )}
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold ${headerBadge.cls}`}>
              {headerBadge.txt}
            </span>
          </div>
        </div>

        {score && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1.5">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-muted text-[11px] font-semibold text-foreground">
                <Star className="h-3 w-3" /> Score {score.score}
              </span>
              <span className="text-[11px] text-muted-foreground font-medium">{score.label}</span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${Math.min(100, Math.max(4, scoreVal))}%`, background: scoreBarGradient }}
              />
            </div>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <DollarSign className="h-4 w-4" />
            <span>Valor Emprestado</span>
          </div>
          <span className="font-semibold text-lg">
            {fmtMoney(amount)}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Percent className="h-4 w-4" />
            <span>Taxa de Juros</span>
          </div>
          <span className="font-semibold">
            {interestRate}%{" "}
            {interestType === "fixed" ? "fixo" : interestType === "daily" ? "(total / diário)" : "ao mês"}
          </span>
        </div>
        
        {interestType === "fixed" && (
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <TrendingUp className="h-4 w-4" />
              <span>Parcelas</span>
            </div>
            <span className="font-semibold">{paidInstallments} / {installments}</span>
          </div>
        )}
        
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="h-4 w-4" />
            <span>Data do Empréstimo</span>
          </div>
          <span className="font-medium">
            {format(loan, "dd/MM/yyyy", { locale: ptBR })}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="h-4 w-4" />
            <span>Vencimento</span>
          </div>
          <span className={actualStatus === "overdue" ? "font-semibold text-overdue" : "font-medium"}>
            {format(due, "dd/MM/yyyy", { locale: ptBR })}
          </span>
        </div>
        
        {interestType === "fixed" && actualStatus !== "paid" && (
          <div className="flex items-center justify-between text-sm rounded-lg bg-primary/5 border border-primary/20 px-3 py-2">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="h-4 w-4 text-primary" />
              <span>Próximo pagamento</span>
            </div>
            <span className="font-semibold text-primary">
              {format(due, "dd/MM/yyyy", { locale: ptBR })} — {fmtMoney(fixedInstallmentAmount)}
            </span>
          </div>
        )}

        {/* Progresso de Pagamento para Juros Fixos */}
        {interestType === "fixed" && actualStatus !== "paid" && (
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 space-y-2 mt-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium">Progresso de Pagamento</span>
              <span className="text-xs font-bold text-primary">{paidInstallments} / {installments}</span>
            </div>
            <div className="w-full bg-muted rounded-full h-1.5">
              <div 
                className="bg-primary h-1.5 rounded-full transition-all" 
                style={{ width: `${(paidInstallments / installments) * 100}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {installments - paidInstallments} {installments - paidInstallments === 1 ? 'parcela restante' : 'parcelas restantes'}
            </p>
          </div>
        )}
        
        {/* Mobile WhatsApp Button - Full Width, Prominent */}
        {showWhatsAppButton && isMobile && (
          <Button
            onClick={handleWhatsAppReminder}
            className={`w-full ${whatsAppConfig.className} text-white font-semibold py-3 text-base flex items-center justify-center gap-2 shadow-md`}
          >
            <MessageCircle className="h-5 w-5" />
            {whatsAppConfig.label}
          </Button>
        )}
        
        {/* Botões de Ação */}
        <div className="pt-2 space-y-2">
          {actualStatus !== "paid" && (
            <Button 
              onClick={() => handleStatusUpdate("paid")}
              disabled={isUpdating}
              className="w-full bg-success hover:bg-success/90"
            >
              {interestType === "fixed" 
                ? `Pagar Parcela ${paidInstallments + 1} de ${installments}` 
                : "Marcar como Pago"}
            </Button>
          )}
          
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="w-full">
                Ver Detalhes e Anotações
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px] max-h-[85vh] flex flex-col">
              <DialogHeader>
                <DialogTitle>Detalhes do Empréstimo</DialogTitle>
                <DialogDescription>
                  Informações completas do empréstimo
                </DialogDescription>
              </DialogHeader>
              
              <ScrollArea className="flex-1 overflow-auto pr-4">
              
              <Tabs defaultValue="details" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="details">Detalhes</TabsTrigger>
                  <TabsTrigger value="payment">Status de Pagamento</TabsTrigger>
                  <TabsTrigger value="notes">Anotações</TabsTrigger>
                </TabsList>
                
                <TabsContent value="details" className="space-y-4 mt-4">
                  <div className="flex items-center gap-3 pb-3 border-b">
                    <div className="rounded-lg bg-primary/10 p-2">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      {isEditingLoan ? (
                        <Input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          placeholder="Nome do cliente"
                          className="max-w-[260px] mb-1"
                        />
                      ) : (
                        <h3 className="font-semibold text-lg">{clientName}</h3>
                      )}
                      <Badge className={displayConfig.className}>{displayConfig.label}</Badge>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    {/* Editar empréstimo */}
                    <div className="flex justify-end mb-2 gap-2">
                      {!isEditingLoan ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={startEditLoan}
                          className="gap-1"
                        >
                          <Edit2 className="h-3 w-3" />
                          Editar Empréstimo
                        </Button>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            onClick={handleSaveLoan}
                            disabled={isSavingLoan}
                            className="gap-1"
                          >
                            <Check className="h-3 w-3" />
                            Salvar Alterações
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={cancelEditLoan}
                            disabled={isSavingLoan}
                            className="gap-1"
                          >
                            <X className="h-3 w-3" />
                            Cancelar
                          </Button>
                        </>
                      )}
                    </div>

                    <div className="flex justify-between items-center gap-3">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="h-4 w-4" />
                        <span>Telefone</span>
                      </div>
                      {isEditingLoan ? (
                        <Input
                          value={editPhone}
                          onChange={(e) => setEditPhone(formatPhoneInput(e.target.value))}
                          placeholder="(99) 99999-9999"
                          className="max-w-[200px]"
                          maxLength={15}
                        />
                      ) : (
                        <span className="font-medium">{maskPhone(phone)}</span>
                      )}
                    </div>
                    
                    <div className="flex justify-between items-center gap-3">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <CreditCard className="h-4 w-4" />
                        <span>CPF</span>
                      </div>
                      {isEditingLoan ? (
                        <Input
                          value={editCpf}
                          onChange={(e) => setEditCpf(formatCpfInput(e.target.value))}
                          placeholder="999.999.999-99"
                          className="max-w-[200px]"
                          maxLength={14}
                        />
                      ) : (
                        <span className="font-medium">{maskCPF(cpf)}</span>
                      )}
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <DollarSign className="h-4 w-4" />
                        <span>Valor Emprestado</span>
                      </div>
                      {isEditingLoan ? (
                        <Input
                          type="text"
                          inputMode="decimal"
                          value={editAmount}
                          onChange={(e) => setEditAmount(e.target.value.replace(/[^\d.,]/g, ""))}
                          placeholder="0,00"
                          className="max-w-[200px]"
                        />
                      ) : (
                        <span className="font-semibold text-lg">
                          {fmtMoney(amount)}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Percent className="h-4 w-4" />
                        <span>Taxa de Juros</span>
                      </div>
                      {isEditingLoan ? (
                        <Input
                          type="text"
                          inputMode="decimal"
                          value={editInterestRate}
                          onChange={(e) => setEditInterestRate(e.target.value.replace(/[^\d.,]/g, ""))}
                          className="max-w-[200px]"
                        />
                      ) : (
                        <span className="font-semibold">{interestRate}% {interestType === "fixed" ? "fixo" : "ao mês"}</span>
                      )}
                    </div>
                    
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <AlertTriangle className="h-4 w-4" />
                          <span>Diária de atraso</span>
                        </div>
                        {isEditingLoan ? (
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={editDailyPenalty}
                            onChange={(e) => setEditDailyPenalty(e.target.value.replace(/[^\d.,]/g, ""))}
                            placeholder="0,00"
                            className="max-w-[200px]"
                          />
                        ) : (
                          <span className={dailyPenalty && dailyPenalty > 0 ? "font-semibold" : "text-muted-foreground"}>
                            {dailyPenalty && dailyPenalty > 0
                              ? `${fmtMoney(dailyPenalty)} /dia`
                              : "Não cobra diária"}
                          </span>
                        )}
                      </div>
                    
                    {interestType === "fixed" && (
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <TrendingUp className="h-4 w-4" />
                          <span>Parcelas Pagas</span>
                        </div>
                        <span className="font-semibold">{paidInstallments} / {installments}</span>
                      </div>
                    )}
                    
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <TrendingUp className="h-4 w-4" />
                        <span>Juros {interestType === "fixed" ? "por Parcela" : "Total"}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-semibold text-lg text-primary">
                          {fmtMoney(totalInterest)}
                        </span>
                        {interestType === "fixed" && (
                          <p className="text-xs text-muted-foreground">
                            {fixedLateCharge > 0
                              ? `${fixedOverdueInstallments} parcela(s) vencida(s) + ${overdueDays} dia(s) de diária`
                              : "Sem juros · sem multa"}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {interestType === "fixed" && (
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <DollarSign className="h-4 w-4" />
                          <span>Valor das Parcelas</span>
                        </div>
                        <span className="font-semibold text-lg">
                          {fmtMoney(fixedInstallmentAmount)}
                        </span>
                      </div>
                    )}

                    {interestType === "fixed" && (
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <DollarSign className="h-4 w-4" />
                          <span>Saldo Restante</span>
                        </div>
                        <span className="font-semibold text-lg">
                          {fmtMoney(fixedRemainingBalance)}
                        </span>
                      </div>
                    )}

                    {interestType === "fixed" && (
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full gap-2"
                        onClick={() => setEditInstallmentsOpen(true)}
                      >
                        <Pencil className="h-4 w-4" />
                        Editar parcelas
                      </Button>
                    )}


                    {actualStatus === "overdue" && (
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Calendar className="h-4 w-4" />
                          <span>Dias em Atraso</span>
                        </div>
                        <span className="font-semibold text-destructive">{overdueDays} dias</span>
                      </div>
                    )}
                    
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span>Data do Empréstimo</span>
                      </div>
                      {isEditingLoan ? (
                        <Input
                          type="date"
                          value={editLoanDate}
                          onChange={(e) => setEditLoanDate(e.target.value)}
                          className="max-w-[200px]"
                        />
                      ) : (
                        <span className="font-medium">
                          {format(loan, "dd/MM/yyyy", { locale: ptBR })}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span>Vencimento</span>
                      </div>
                      {isEditingLoan ? (
                        <Input
                          type="date"
                          value={editDueDate}
                          min={editLoanDate}
                          onChange={(e) => setEditDueDate(e.target.value)}
                          className="max-w-[200px]"
                        />
                      ) : (
                        <span className={actualStatus === "overdue" ? "font-semibold text-overdue" : "font-medium"}>
                          {format(due, "dd/MM/yyyy", { locale: ptBR })}
                        </span>
                      )}
                    </div>

                    {/* Juros Total a Pagar (apenas para atrasados) */}
                    {actualStatus === "overdue" && interestType === "monthly" && (
                      <div className="mt-3 p-4 rounded-lg bg-destructive/10 border-2 border-destructive/30 space-y-2">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="h-5 w-5 text-destructive" />
                          <h4 className="font-bold text-sm uppercase text-destructive">Juros Total a Pagar</h4>
                        </div>
                        
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Juros mensal ({interestRate}%):</span>
                          <span className="font-medium">
                            {fmtMoney(monthlyInterest)}
                          </span>
                        </div>
                        
                        <div className="flex justify-between pt-2 border-t border-destructive/30">
                          <span className="font-bold">Total de Juros:</span>
                          <span className="font-bold text-destructive text-lg">
                            {fmtMoney(monthlyInterest)}
                          </span>
                        </div>
                        
                        <div className="flex justify-between text-sm pt-2">
                          <span className="text-muted-foreground">Total de Juros (Atualizado):</span>
                          <span className="font-bold text-destructive">
                            {fmtMoney(((amount * (interestRate / 100) * overdueMonths) + (overdueDays * ((dailyPenalty && dailyPenalty > 0) ? dailyPenalty : 10))))}
                          </span>
                        </div>
                        
                        <div className="flex justify-between pt-2 border-t border-destructive/30">
                          <span className="font-bold text-lg">Total Para Terminar:</span>
                          <span className="font-bold text-destructive text-xl">
                            {fmtMoney((amount + (amount * (interestRate / 100) * overdueMonths) + (overdueDays * ((dailyPenalty && dailyPenalty > 0) ? dailyPenalty : 10))))}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Painel de destaque — Modalidade Diária */}
                    {interestType === "daily" && (
                      <div className="mt-3 p-4 rounded-lg bg-primary/10 border-2 border-primary/30 space-y-3">
                        <div className="flex items-center gap-2 mb-1">
                          <DollarSign className="h-5 w-5 text-primary" />
                          <h4 className="font-bold text-sm uppercase text-primary">Cobrança por Ciclo</h4>
                        </div>

                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Ciclo combinado:</span>
                          <span className="font-medium">{dailyCycleDays} dia(s)</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Juros por ciclo ({interestRate}%):</span>
                          <span className="font-medium">
                            {fmtMoney(dailyInterestPerCycle)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Ciclos vencidos:</span>
                          <span className="font-medium">{dailyCyclesElapsed}x</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Juros vencidos dos ciclos:</span>
                          <span className="font-medium">
                            {fmtMoney(dailyOverdueInterest)}
                          </span>
                        </div>
                        {overdueDays > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Diárias de atraso ({overdueDays} × {fmtMoney(dailyPenalty || 0)}):
                            </span>
                            <span className="font-medium text-destructive">
                              {fmtMoney(dailyLatePenalty)}
                            </span>
                          </div>
                        )}

                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Total juros + diárias:</span>
                          <span className="font-medium">
                            {fmtMoney(dailyTotalCharges)}
                          </span>
                        </div>

                        <div className="flex justify-between pt-3 border-t border-primary/30">
                          <span className="font-bold text-base">Total para quitar:</span>
                          <span className="font-bold text-primary text-xl">
                            {fmtMoney(dailyTotalToSettle)}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Cálculo Automático (apenas Mensal/Fixo) */}
                    {interestType !== "daily" && (
                    <div className="mt-4 p-4 rounded-lg bg-primary/5 border border-primary/20 space-y-3">
                      <div className="flex items-center gap-2 mb-3">
                        <DollarSign className="h-4 w-4 text-primary" />
                        <h4 className="font-semibold text-sm">Cálculo Automático</h4>
                      </div>
                      
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Valor emprestado:</span>
                        <span className="font-medium">
                          {fmtMoney(amount)}
                        </span>
                      </div>
                      
                      {interestType !== "fixed" && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Juros mensal ({interestRate}%):</span>
                          <span className="font-medium text-warning">
                            {fmtMoney(monthlyInterest)}
                          </span>
                        </div>
                      )}
                      
                      {interestType === "fixed" && (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Parcelas restantes:</span>
                            <span className="font-medium">
                              {installments - paidInstallments}
                            </span>
                          </div>
                          
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Valor por parcela:</span>
                            <span className="font-medium">
                              {fmtMoney(fixedInstallmentAmount)}
                            </span>
                          </div>
                          {fixedLateCharge > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Parcelas vencidas ({fixedOverdueInstallments}x):</span>
                              <span className="font-medium text-destructive">{fmtMoney(fixedOverdueInstallments * fixedInstallmentAmount)}</span>
                            </div>
                          )}
                          {fixedLateCharge > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Diária de atraso ({overdueDays} dia(s)):</span>
                              <span className="font-medium text-destructive">{fmtMoney(overdueDays * (dailyPenalty || 0))}</span>
                            </div>
                          )}
                        </>
                      )}
                      
                      <div className="flex justify-between pt-2 border-t border-primary/20">
                        <span className="font-semibold">Total a receber {interestType === "fixed" ? "nesta parcela" : ""}:</span>
                        <span className="font-bold text-success text-lg">
                          {interestType === "fixed" 
                            ? fmtMoney(fixedInstallmentAmount + fixedLateCharge)
                            : fmtMoney(amount + monthlyInterest)
                          }
                        </span>
                      </div>
                    </div>
                    )}
                  </div>
                </TabsContent>
                
                <TabsContent value="payment" className="space-y-4 mt-4">
                  <div className="space-y-4">
                    <div className="text-center space-y-2 py-4">
                      <h4 className="font-semibold text-lg">Status Atual</h4>
                      <Badge className={`${displayConfig.className} text-lg px-4 py-2`}>
                        {displayConfig.label}
                      </Badge>
                    </div>
                    
                    {interestType === "fixed" && actualStatus !== "paid" && (
                      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Progresso de Pagamento</span>
                          <span className="text-sm font-bold text-primary">{paidInstallments} / {installments}</span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2">
                          <div 
                            className="bg-primary h-2 rounded-full transition-all" 
                            style={{ width: `${(paidInstallments / installments) * 100}%` }}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {installments - paidInstallments} {installments - paidInstallments === 1 ? 'parcela restante' : 'parcelas restantes'}
                        </p>
                      </div>
                    )}
                    
                    <div className="border-t pt-4 space-y-3">
                      <p className="text-sm text-muted-foreground text-center">
                        {interestType === "fixed" 
                          ? "Marcar parcela como paga" 
                          : "Alterar o status do empréstimo"}
                      </p>
                      
                      {actualStatus !== "paid" && (
                        <Button 
                          onClick={() => handleStatusUpdate("paid")}
                          disabled={isUpdating}
                          className="w-full bg-success hover:bg-success/90"
                          size="lg"
                        >
                          {interestType === "fixed" 
                            ? `Pagar Parcela ${paidInstallments + 1} de ${installments}` 
                            : "Marcar como Pago"}
                        </Button>
                      )}

                      {hasUndoPayment && (
                        <Button
                          variant="outline"
                          className="w-full"
                          disabled={isUpdating}
                          onClick={handleUndoPayment}
                        >
                          Desfazer Pagamento
                        </Button>
                      )}
                      
                      {actualStatus === "paid" && (
                        <div className="text-center space-y-3 py-4">
                          <p className="text-muted-foreground">
                            {interestType === "fixed" 
                              ? "Todas as parcelas foram pagas!" 
                              : "Este empréstimo já foi marcado como pago."}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="notes" className="mt-4">
                  <LoanNotes loanId={id} clientName={clientName} />
                </TabsContent>
              </Tabs>
              </ScrollArea>
              
              <DialogFooter className="gap-2 sm:gap-0 border-t pt-4">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" className="gap-2">
                      <Trash2 className="h-4 w-4" />
                      Remover Empréstimo
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Remover empréstimo?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Esta ação não pode ser desfeita. O empréstimo de {clientName} será removido permanentemente do sistema.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDelete} disabled={isUpdating}>Remover</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {interestType === "fixed" && (
            <EditInstallmentsDialog
              open={editInstallmentsOpen}
              onOpenChange={setEditInstallmentsOpen}
              loanId={id}
              amount={amount}
              installments={installments}
              paidInstallments={paidInstallments}
              installmentValues={installmentValues}
              nextDueDate={due}
              installmentFrequency={installmentFrequency}
              onSaved={() => onDelete?.()}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}
